import React from 'react';
import useModal from '@/hooks/useModal';
import { ModalHeader } from '../../../modals/ModalHeader';
import PdfViewer, {
	PdfViewerStatus,
} from '@/components/common/pdf-viewer/PdfViewer';
import {
	actionRequiredService,
	GET_CLIENT_ACCOUNT_DETAILS,
	GET_REVIEW_SIGN_DETAILS,
} from '@/api';
import { clientProfileService } from '@/api/clientProfile';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ContentTitle } from '../ContentTitle';
import { DocumentInformationSection } from './DocumentInformationSection';
import { SignReviewPanel } from './SignReviewPanel';
import { ReplyPanel } from './ReplyPanel';
import {
	buildSignReviewComparison,
	getReviewSourceFields,
	hasSignReviewComparison,
} from '@/lib/signReviewComparison';
import {
	PdfViewerControls,
	PdfViewerDownloadControl,
} from './PdfViewerControls';
import { LoaderScreen, Typography } from '@/components/common';
import { useChatBoxLawyer } from '@/hooks/useChatBoxLawyer';
import { DocumentItemData } from '@/interfaces/clientDocumentation';
import { SignDocumentDetails } from '@/interfaces';
import { GET_CLIENT_PROFILE_FILES } from '@/api';
import { differenceInDays, parseISO } from 'date-fns';
import { useNotification } from '@/hooks';
import { formatErrorMessage } from '@/lib';

interface Props {
	isOpen: boolean;
	actionRequiredId: number | undefined;
	showDocumentControls?: boolean;
	isClientView?: boolean;
	userId?: string;
	document?: DocumentItemData;
	urlDoc?: string;
	versionDetails?: SignDocumentDetails;
	setIsModalOpen: (value: boolean) => void;
}

export const ModalReviewSignLawyer: React.FC<Props> = ({
	actionRequiredId,
	showDocumentControls = false,
	isClientView = false,
	userId,
	document,
	urlDoc,
	versionDetails,
	isOpen,
	setIsModalOpen,
}) => {
	const { toastSuccess, toastError } = useNotification();
	const { Modal, show, hide } = useModal();
	const [view, setView] = React.useState<'document' | 'reply'>('document');
	const [message, setMessage] = React.useState('');
	const [requestResubmit, setRequestResubmit] = React.useState(false);
	const [messageSentInSession, setMessageSentInSession] = React.useState(false);
	const [resubmitRequestedOnSend, setResubmitRequestedOnSend] =
		React.useState(false);
	const [pendingShowMessage, setPendingShowMessage] = React.useState(true);
	const [localStatus, setLocalStatus] = React.useState(document?.status);
	const [localLastMessage, setLocalLastMessage] = React.useState(
		document?.last_message
	);
	const queryClient = useQueryClient();

	const { onSubmit } = useChatBoxLawyer({ clientId: userId ?? '' });

	const isVersionView = !!versionDetails;

	const {
		data: fetchedDetails,
		isLoading,
		isError,
		refetch: refetchDetails,
	} = useQuery({
		queryKey: [GET_REVIEW_SIGN_DETAILS, actionRequiredId],
		queryFn: () =>
			actionRequiredService.getActionRequireReviewSign(
				String(actionRequiredId)
			),
		enabled: !isVersionView,
	});
	const actionDetails = React.useMemo(
		() =>
			versionDetails
				? { statusCode: 200, message: '', data: versionDetails }
				: fetchedDetails,
		[versionDetails, fetchedDetails]
	);

	const hasQueryError = isError && !actionDetails?.data;
	const isLawyerReview =
		(showDocumentControls || isVersionView) &&
		(hasSignReviewComparison(actionDetails?.data) || hasQueryError);
	const [pdfStatus, setPdfStatus] = React.useState<PdfViewerStatus>('loading');
	const { data: accountDetails } = useQuery({
		queryKey: [GET_CLIENT_ACCOUNT_DETAILS, userId],
		queryFn: () => clientProfileService.getAccountDetails(userId as string),
		enabled: isLawyerReview && !!userId,
	});
	const comparison = React.useMemo(
		() => buildSignReviewComparison(actionDetails?.data, accountDetails?.data),
		[actionDetails, accountDetails]
	);

	const isSignRequired = actionDetails?.data.is_signature_required;
	const documentSignature = actionDetails?.data.signature ?? '';
	const documentUrl = urlDoc ?? actionDetails?.data.url_doc ?? '';
	const fields = getReviewSourceFields(actionDetails?.data);
	const name =
		actionDetails?.data.name_doc ??
		(isLawyerReview ? document?.title : undefined) ??
		'Document Name';
	const signDate = actionDetails?.data.signature
		? actionDetails?.data.signed_at ?? ''
		: '';
	const pastDays = document?.dateCompleted
		? differenceInDays(new Date(), parseISO(document.dateCompleted))
		: undefined;
	const pastDaysLabel =
		pastDays === undefined
			? null
			: pastDays === 0
			? 'Today'
			: `Uploaded ${pastDays} day${pastDays === 1 ? '' : 's'} ago`;

	const wasOpenRef = React.useRef(false);

	React.useEffect(() => {
		if (isOpen) {
			show();
			if (!wasOpenRef.current) {
				setMessageSentInSession(false);
				setResubmitRequestedOnSend(false);
				setPendingShowMessage(true);
				setLocalStatus(document?.status);
				setLocalLastMessage(document?.last_message);
			}
		}
		wasOpenRef.current = isOpen;
	}, [isOpen, document]);

	const handleCloseModal = (): void => {
		hide();
		setIsModalOpen(false);
	};

	const handleReviewSignature = async (validate: boolean) => {
		if (!actionRequiredId) return;
		try {
			await actionRequiredService.reviewSignatureDocument(
				actionRequiredId,
				validate
			);
			if (isPending && pendingShowMessage) {
				setView('reply');
			}
			setLocalStatus(validate ? 'VALIDATED' : 'INVALID');
			queryClient.refetchQueries({
				queryKey: [GET_REVIEW_SIGN_DETAILS, actionRequiredId],
			});
			queryClient.invalidateQueries({ queryKey: [GET_CLIENT_PROFILE_FILES] });
			toastSuccess(
				validate
					? 'The document has been successfully validated.'
					: 'The document has been successfully invalidated'
			);
			await new Promise((resolve) => setTimeout(resolve, 800));
			handleCloseModal();
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};

	const handleSendMessage = async () => {
		if (!message.trim() || !document) return;
		onSubmit({
			message,
			is_bot: false,
			action_required_id: requestResubmit ? actionRequiredId : undefined,
			message_id: null,
			document_name: document.title,
		});
		if (requestResubmit) {
			setLocalStatus('PENDING');
			setLocalLastMessage({
				content: message,
				created_at: new Date().toISOString(),
			});
		}
		await new Promise((resolve) => setTimeout(resolve, 500));
		queryClient.invalidateQueries({ queryKey: [GET_CLIENT_PROFILE_FILES] });
		setMessage('');
		setMessageSentInSession(true);
		setResubmitRequestedOnSend(requestResubmit);
	};

	const isPending = localStatus?.toLowerCase() === 'pending';
	const isInvalid = ['invalid', 'invalidated'].includes(
		localStatus?.toLowerCase() ?? ''
	);
	const isValidated = ['validated', 'valid'].includes(
		localStatus?.toLowerCase() ?? ''
	);
	const lastMessage = localLastMessage;

	const isDocumentViewMounted =
		view === 'document' &&
		(!isPending || !pendingShowMessage) &&
		Boolean(documentUrl);
	const panelStatus = isLoading ? 'loading' : hasQueryError ? 'error' : 'ready';
	const reviewActionsDisabled =
		isLoading ||
		hasQueryError ||
		(isDocumentViewMounted && pdfStatus !== 'ready');
	const toolbarButtonClassName = isLawyerReview
		? 'font-semibold text-base !h-[30px] !py-0 shrink-0'
		: undefined;

	return (
		<Modal
			classNameCardContainer="!bg-gradient-to-r from-white to-white md:w-full max-w-[1256px] h-[90vh] relative flex flex-col overflow-visible"
			withXButton
			closeIconTopPosition="31px"
			title={
				<ModalHeader
					title={<ContentTitle title={name} time={signDate} />}
					classNameContent="!justify-start"
					classNameImage="!block"
				/>
			}
			onClose={handleCloseModal}
			classNameContainerChildren="flex-1 min-h-0 xl:!max-h-[78vh] md:h-[calc(90vh-139px)] no-scrollbar-mobile lg:!overflow-visible"
			classNameFooter="shrink-0 md:pt-6"
		>
			{isLoading ? (
				<LoaderScreen />
			) : (
				<section className="flex flex-col md:flex-row gap-6 h-full min-h-0">
					<div className="flex-1 min-w-0 h-full min-h-min lg:min-h-0">
						{view === 'document' && isPending && pendingShowMessage && (
							<ReplyPanel
								backLabel="Back to document"
								onBack={() => setPendingShowMessage(false)}
								documentUrl={documentUrl}
								fileName={name}
								documentTitle={document?.title}
								lastMessage={lastMessage}
								showReplyForm={false}
								message={message}
								onMessageChange={setMessage}
								onSendMessage={handleSendMessage}
								requestResubmit={requestResubmit}
								onRequestResubmitChange={setRequestResubmit}
							/>
						)}

						{view === 'document' &&
							(!isPending || !pendingShowMessage) &&
							documentUrl && (
								<PdfViewer
									showControls
									showZoom
									heightClassName="h-full"
									pdfUrl={documentUrl}
									onStatusChange={isLawyerReview ? setPdfStatus : undefined}
									extraControls={
										isPending ? (
											<PdfViewerControls
												leftButtonLabel="Back to messages"
												onLeftButtonClick={() => setPendingShowMessage(true)}
												className={toolbarButtonClassName}
											/>
										) : showDocumentControls ? (
											<PdfViewerControls
												leftButtonLabel="Reply to Client"
												onLeftButtonClick={() => setView('reply')}
												className={toolbarButtonClassName}
											/>
										) : undefined
									}
									rightControls={
										isPending || showDocumentControls || isVersionView ? (
											<PdfViewerDownloadControl
												documentUrl={documentUrl}
												fileName={name}
												className={toolbarButtonClassName}
											/>
										) : undefined
									}
								/>
							)}

						{isLawyerReview &&
							hasQueryError &&
							view === 'document' &&
							(!isPending || !pendingShowMessage) &&
							!documentUrl && (
								<div className="h-full border border-grey-100 rounded-2xl flex items-center justify-center p-4">
									<Typography type="regular-14" className="text-gray-35">
										We couldn&apos;t load the document.
									</Typography>
								</div>
							)}

						{view === 'reply' && document && (
							<ReplyPanel
								backLabel="Back to document"
								onBack={() => setView('document')}
								documentUrl={documentUrl}
								fileName={name}
								documentTitle={document.title}
								pastDaysLabel={pastDaysLabel}
								lastMessage={lastMessage}
								showReplyForm={!isValidated && !isPending}
								message={message}
								onMessageChange={setMessage}
								onSendMessage={handleSendMessage}
								requestResubmit={requestResubmit}
								onRequestResubmitChange={setRequestResubmit}
							/>
						)}
					</div>

					<div
						className={
							isLawyerReview
								? 'w-full md:w-[280px] xl:w-[276px] shrink-0 md:h-full min-h-0'
								: 'lg:w-[30%] 2xl:w-[23%] shrink-0 h-full overflow-y-auto scroll-custom space-y-4'
						}
					>
						{isLawyerReview ? (
							<SignReviewPanel
								comparison={comparison}
								status={panelStatus}
								actionsDisabled={reviewActionsDisabled}
								validateDisabled={
									isValidated ||
									(messageSentInSession && resubmitRequestedOnSend)
								}
								invalidateDisabled={
									isInvalid || (messageSentInSession && resubmitRequestedOnSend)
								}
								onValidate={() => handleReviewSignature(true)}
								onInvalidate={() => handleReviewSignature(false)}
								onRetry={() => refetchDetails()}
								hideActions={isVersionView}
							/>
						) : (
							<DocumentInformationSection
								actionDetails={actionDetails}
								isSignRequired={isSignRequired}
								documentSignature={documentSignature}
								fields={fields}
								fillHeight={isClientView}
								showValidateButton={showDocumentControls}
								validateDisabled={
									isValidated ||
									(messageSentInSession && resubmitRequestedOnSend)
								}
								onValidate={() => handleReviewSignature(true)}
								invalidateDisabled={
									isInvalid || (messageSentInSession && resubmitRequestedOnSend)
								}
								onInvalidate={() => handleReviewSignature(false)}
							/>
						)}
					</div>
				</section>
			)}
		</Modal>
	);
};

export default ModalReviewSignLawyer;
